import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  CheckCircle, Clock, AlertCircle, ArrowRight, Package, 
  TestTube, Rocket, Shield, ChevronRight 
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

export function TransportLifecycleTracker({ lifecycle, onAction }) {
  const [actionDialog, setActionDialog] = useState(false);
  const [currentAction, setCurrentAction] = useState(null);
  const [actionNotes, setActionNotes] = useState('');
  const [testResults, setTestResults] = useState(null);

  if (!lifecycle) return null;

  const getEnvironmentProgress = () => {
    if (lifecycle.overall_status === 'completed' || lifecycle.current_environment === 'prod') return 100;
    if (lifecycle.current_environment === 'qa') return 66;
    if (lifecycle.current_environment === 'dev') return 33;
    return 0;
  };

  const getStatusIcon = (status) => {
    if (status === 'passed' || status === 'deployed' || status === 'completed') {
      return <CheckCircle className="h-4 w-4 text-cp-ok" />;
    }
    if (status === 'in_progress' || status === 'testing') {
      return <Clock className="h-4 w-4 text-cp-indigo animate-pulse" />;
    }
    if (status === 'failed' || status === 'blocked') {
      return <AlertCircle className="h-4 w-4 text-cp-crit" />;
    }
    return <Clock className="h-4 w-4 text-cp-faint" />;
  };

  const getTestStatusBadge = (testStatus) => {
    const variants = {
      not_started: { variant: 'outline', label: 'Not Started' },
      in_progress: { variant: 'default', label: 'Testing' },
      passed: { variant: 'default', label: 'Passed', className: 'bg-cp-ok' },
      failed: { variant: 'destructive', label: 'Failed' },
      blocked: { variant: 'destructive', label: 'Blocked' }
    };
    
    const config = variants[testStatus] || variants.not_started;
    return <Badge variant={config.variant} className={config.className}>{config.label}</Badge>;
  };

  const handleAction = async (action, requiresInput = false) => {
    if (requiresInput) {
      setCurrentAction(action);
      setActionDialog(true);
    } else {
      await onAction({ action });
    }
  };

  const submitAction = async () => {
    const payload = {
      action: currentAction,
      notes: actionNotes
    };
    
    if (currentAction.includes('testing')) {
      payload.test_results = testResults || {
        passed: true,
        test_cases: 10,
        passed_cases: 10,
        failed_cases: 0
      };
    }
    
    await onAction(payload);
    setActionDialog(false);
    setCurrentAction(null);
    setActionNotes('');
    setTestResults(null);
  };

  return (
    <div className="space-y-4">
      {/* Overall Progress */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-cp-indigo" />
            Transport Lifecycle
          </CardTitle>
          <CardDescription>
            Track customization movement through DEV → QA → PROD
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Progress Bar */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Overall Progress</span>
                <Badge>{lifecycle.overall_status.replace('_', ' ').toUpperCase()}</Badge>
              </div>
              <Progress value={getEnvironmentProgress()} className="h-2" />
            </div>

            {/* Environment Timeline */}
            <div className="grid grid-cols-3 gap-4">
              {/* DEV Environment */}
              <div className={`p-4 border-2 rounded-lg ${lifecycle.current_environment === 'dev' ? 'border-cp-indigo bg-cp-inset' : 'border-cp-line'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(lifecycle.dev_status.status)}
                    <span className="font-semibold text-sm">DEV</span>
                  </div>
                  {lifecycle.dev_status.code_complete && (
                    <CheckCircle className="h-4 w-4 text-cp-ok" />
                  )}
                </div>
                
                <div className="text-xs text-muted-foreground mb-2">{lifecycle.dev_status.status.replace('_', ' ')}</div>
                
                {lifecycle.dev_status.test_status !== 'not_started' && (
                  <div className="mb-2">
                    {getTestStatusBadge(lifecycle.dev_status.test_status)}
                  </div>
                )}

                {/* DEV Actions */}
                <div className="space-y-1 mt-2">
                  {!lifecycle.dev_status.code_complete && (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="w-full text-xs"
                      onClick={() => handleAction('complete_dev')}
                    >
                      Mark Code Complete
                    </Button>
                  )}
                  
                  {lifecycle.dev_status.code_complete && lifecycle.dev_status.test_status === 'in_progress' && (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="w-full text-xs"
                      onClick={() => handleAction('complete_dev_testing', true)}
                    >
                      Complete Testing
                    </Button>
                  )}
                  
                  {lifecycle.dev_status.test_status === 'passed' && !lifecycle.dev_status.transport_request_id && (
                    <Button 
                      size="sm" 
                      className="w-full text-xs"
                      onClick={() => handleAction('create_transport_to_qa', true)}
                    >
                      Create Transport to QA
                    </Button>
                  )}
                </div>
              </div>

              {/* Arrow */}
              <div className="flex items-center justify-center">
                <ArrowRight className={`h-6 w-6 ${lifecycle.dev_status.transport_request_id ? 'text-cp-ok' : 'text-cp-faint'}`} />
              </div>

              {/* QA Environment */}
              <div className={`p-4 border-2 rounded-lg ${lifecycle.current_environment === 'qa' ? 'border-cp-indigo bg-cp-inset' : 'border-cp-line'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(lifecycle.qa_status.status)}
                    <span className="font-semibold text-sm">QA</span>
                  </div>
                  {lifecycle.qa_status.deployed_at && (
                    <CheckCircle className="h-4 w-4 text-cp-ok" />
                  )}
                </div>
                
                <div className="text-xs text-muted-foreground mb-2">{lifecycle.qa_status.status.replace('_', ' ')}</div>
                
                {lifecycle.qa_status.test_status !== 'not_started' && (
                  <div className="mb-2">
                    {getTestStatusBadge(lifecycle.qa_status.test_status)}
                  </div>
                )}

                {/* QA Actions */}
                <div className="space-y-1 mt-2">
                  {lifecycle.dev_status.transport_request_id && lifecycle.qa_status.status === 'not_started' && (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="w-full text-xs"
                      onClick={() => handleAction('import_to_qa', true)}
                    >
                      Import Transport
                    </Button>
                  )}
                  
                  {lifecycle.qa_status.test_status === 'in_progress' && (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="w-full text-xs"
                      onClick={() => handleAction('complete_qa_testing', true)}
                    >
                      Complete QA Testing
                    </Button>
                  )}
                  
                  {lifecycle.qa_status.test_status === 'passed' && !lifecycle.qa_status.transport_request_id && (
                    <Button 
                      size="sm" 
                      className="w-full text-xs"
                      onClick={() => handleAction('create_transport_to_prod', true)}
                    >
                      Create Transport to PROD
                    </Button>
                  )}
                </div>
              </div>

              {/* Arrow */}
              <div className="flex items-center justify-center">
                <ArrowRight className={`h-6 w-6 ${lifecycle.qa_status.transport_request_id ? 'text-cp-ok' : 'text-cp-faint'}`} />
              </div>

              {/* PROD Environment */}
              <div className={`p-4 border-2 rounded-lg ${lifecycle.current_environment === 'prod' ? 'border-cp-indigo bg-cp-inset' : 'border-cp-line'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(lifecycle.prod_status.status)}
                    <span className="font-semibold text-sm">PROD</span>
                  </div>
                  {lifecycle.prod_status.deployed_at && (
                    <Rocket className="h-4 w-4 text-cp-ok" />
                  )}
                </div>
                
                <div className="text-xs text-muted-foreground mb-2">{lifecycle.prod_status.status.replace('_', ' ')}</div>

                {/* PROD Actions */}
                <div className="space-y-1 mt-2">
                  {lifecycle.qa_status.transport_request_id && lifecycle.prod_status.status === 'not_started' && (
                    <Button 
                      size="sm" 
                      variant="default"
                      className="w-full text-xs bg-cp-ok hover:bg-cp-ok"
                      onClick={() => handleAction('import_to_prod', true)}
                    >
                      <Shield className="h-3 w-3 mr-1" />
                      Import to PROD
                    </Button>
                  )}
                  
                  {lifecycle.prod_status.deployed_at && (
                    <Badge variant="default" className="w-full justify-center bg-cp-ok">
                      Live in Production
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Timeline Details */}
            {lifecycle.dev_completed_at && (
              <div className="text-xs text-muted-foreground">
                <div>DEV Completed: {new Date(lifecycle.dev_completed_at).toLocaleString()}</div>
                {lifecycle.qa_completed_at && (
                  <div>QA Completed: {new Date(lifecycle.qa_completed_at).toLocaleString()}</div>
                )}
                {lifecycle.prod_completed_at && (
                  <div>PROD Deployed: {new Date(lifecycle.prod_completed_at).toLocaleString()}</div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Action Dialog */}
      <Dialog open={actionDialog} onOpenChange={setActionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete Action</DialogTitle>
            <DialogDescription>
              Provide details for: {currentAction?.replace('_', ' ')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Notes</Label>
              <Textarea
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
                placeholder="Add any notes or comments..."
                rows={3}
              />
            </div>

            {currentAction?.includes('testing') && (
              <div>
                <Label>Test Results</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Button
                    variant={testResults?.passed ? 'default' : 'outline'}
                    onClick={() => setTestResults({ passed: true, test_cases: 10, passed_cases: 10, failed_cases: 0 })}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Tests Passed
                  </Button>
                  <Button
                    variant={testResults?.passed === false ? 'destructive' : 'outline'}
                    onClick={() => setTestResults({ passed: false, test_cases: 10, passed_cases: 7, failed_cases: 3 })}
                  >
                    <AlertCircle className="h-4 w-4 mr-2" />
                    Tests Failed
                  </Button>
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <Button onClick={submitAction} className="flex-1">
                Submit
              </Button>
              <Button variant="outline" onClick={() => setActionDialog(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
